// 채팅 첨부 업로드/삭제 — Supabase Edge Function 으로 presign 받아 R2 에 PUT.
// presign 발급 / 삭제는 Edge Function (서버) 에서 R2 secret 보호.
import { supabase } from '../supabaseClient';

/**
 * 한 묶음의 파일에 대해 presigned URL 받아온다.
 * @param {object} opts
 * @param {'project_chat'|'dm'|'post'|'comment'} opts.kind
 * @param {string} [opts.projectId] - project_chat / post / comment 일 때 필수
 * @param {string} [opts.threadId] - dm 일 때 필수
 * @param {string} [opts.taskId] - post / comment 일 때 필수
 * @param {string} [opts.postId] - comment 일 때 필수
 * @param {Array<{name:string,type:string,size:number}>} opts.files
 * @returns {Promise<{sessionId:string, files:Array<{ok:boolean, key?:string, url?:string, name?:string, type?:string, size?:number, error?:string}>}>}
 */
export async function requestPresign({ kind, projectId, threadId, taskId, postId, files }) {
  console.log('[chatUpload] presign 요청', {
    kind,
    projectId,
    threadId,
    taskId,
    postId,
    files: files?.map((f) => ({ name: f.name, type: f.type, size: f.size })),
  });
  // supabase-js 의 functions.invoke 는 비-2xx 응답 body 를 못 풀어주는 경우가 있어
  // 직접 fetch 로 호출해 raw body 를 확보한다.
  const supabaseUrl = process.env.REACT_APP_SUPABASE_URL;
  const supabaseAnonKey = process.env.REACT_APP_SUPABASE_ANON_KEY;
  const session = await supabase.auth.getSession();
  const accessToken = session.data.session?.access_token;
  if (!accessToken) {
    throw new Error('로그인이 필요합니다');
  }
  let res;
  try {
    res = await fetch(`${supabaseUrl}/functions/v1/chat-attachments-presign`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${accessToken}`,
        apikey: supabaseAnonKey,
      },
      body: JSON.stringify({ kind, projectId, threadId, taskId, postId, files }),
    });
  } catch (e) {
    console.error('[chatUpload] presign 네트워크 에러', e);
    throw e;
  }
  const text = await res.text();
  let parsed = null;
  try {
    parsed = text ? JSON.parse(text) : null;
  } catch {
    // not JSON
  }
  console.log('[chatUpload] presign 응답', {
    status: res.status,
    ok: res.ok,
    body: parsed || text,
  });
  if (!res.ok) {
    throw new Error(
      (parsed && (parsed.error || parsed.detail)) ||
        text ||
        `HTTP ${res.status}`
    );
  }
  if (parsed?.error) {
    throw new Error(parsed.error);
  }
  return parsed;
}

/**
 * R2 객체 삭제 (서버에 위임).
 * 사용처: 전송 전 칩 제거 / 메시지 삭제 / 수정에서 첨부 일부 제거.
 * 실패해도 사용자 메시지 흐름은 막지 않음 — 야간 cron 으로 orphan 청소 가능.
 */
export async function deleteAttachments(keys) {
  if (!Array.isArray(keys) || keys.length === 0) return { ok: true };
  try {
    const { data, error } = await supabase.functions.invoke(
      'chat-attachments-delete',
      { body: { keys } }
    );
    if (error) throw error;
    return data || { ok: true };
  } catch (e) {
    console.warn('R2 삭제 실패:', e?.message);
    return { ok: false, error: e?.message };
  }
}

/**
 * 단일 파일을 presigned URL 로 R2 에 PUT. 진행률·취소 지원.
 * @param {object} opts
 * @param {File} opts.file - 브라우저 File 객체
 * @param {string} opts.url - presigned PUT URL
 * @param {string} opts.type - Content-Type (presign 시 박힌 값과 동일해야 함)
 * @param {(percent:number)=>void} [opts.onProgress]
 * @returns {{ promise: Promise<void>, abort: ()=>void }}
 */
export function uploadFileToR2({ file, url, type, onProgress }) {
  const xhr = new XMLHttpRequest();
  const promise = new Promise((resolve, reject) => {
    xhr.open('PUT', url, true);
    xhr.setRequestHeader('Content-Type', type || file.type || 'application/octet-stream');
    xhr.upload.onprogress = (e) => {
      if (!e.lengthComputable || !onProgress) return;
      const pct = Math.round((e.loaded / e.total) * 100);
      onProgress(pct);
    };
    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        if (onProgress) onProgress(100);
        resolve();
      } else {
        reject(new Error(`업로드 실패 (${xhr.status})`));
      }
    };
    xhr.onerror = () => reject(new Error('네트워크 오류'));
    xhr.onabort = () => reject(new Error('aborted'));
    xhr.send(file);
  });
  return {
    promise,
    abort: () => xhr.abort(),
  };
}

/**
 * 메시지 attachments 메타에서 R2 key 만 추출 (삭제 호출용).
 */
export function extractKeys(attachments) {
  if (!Array.isArray(attachments)) return [];
  return attachments.map((a) => a?.key).filter(Boolean);
}
